import { useEffect, useRef } from "react";
import { create } from "zustand";
import type { OrbStateId } from "./afterimage/orb";

/** The finishing beat of a turn: done for now (the spin, whirl, is kept but unused). */
export type AskOpusBeat = "done" | "whirl";

// How long a header orb shows a turn's finishing beat before it rests.
export const ASK_OPUS_DONE_MS = 2600;
export const ASK_OPUS_WHIRL_MS = 2300;

/** The key Ask Opus's own orbs use; agents use their id. */
export const ASK_OPUS_ORB_KEY = "ask-opus";

type Beat = { turnId: string; kind: AskOpusBeat };

type OrbStateStore = {
  states: Record<string, OrbStateId>;
  /** Per key: the turn whose beat last played, so it isn't repeated. */
  beats: Record<string, Beat>;
  setState: (key: string, state: OrbStateId) => void;
  /** Play a turn's finishing beat, then rest. */
  playBeat: (key: string, turnId: string | null, kind: AskOpusBeat) => void;
};

const restTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * What the orbs on the page show, per chat: Ask Opus (ASK_OPUS_ORB_KEY) and
 * each agent (its id). The open panel drives its key from its chat through
 * `useDriveOrb`, and the chat row starts a turn's finishing beat through
 * `playBeat`; the rail entry, top-nav button and panel header only read it.
 */
export const useOrbStateStore = create<OrbStateStore>()((set) => ({
  states: {},
  beats: {},
  setState: (key, state) => {
    clearTimeout(restTimers.get(key));
    set((s) => ({ states: { ...s.states, [key]: state } }));
  },
  playBeat: (key, turnId, kind) => {
    clearTimeout(restTimers.get(key));
    set((s) => ({
      states: { ...s.states, [key]: kind },
      beats: turnId ? { ...s.beats, [key]: { turnId, kind } } : s.beats,
    }));
    restTimers.set(
      key,
      setTimeout(
        () => set((s) => ({ states: { ...s.states, [key]: "idle" } })),
        kind === "whirl" ? ASK_OPUS_WHIRL_MS : ASK_OPUS_DONE_MS,
      ),
    );
  },
}));

export const useOrbState = (key: string): OrbStateId =>
  useOrbStateStore((s) => s.states[key] ?? "idle");

export const useAskOpusOrbState = (): OrbStateId =>
  useOrbState(ASK_OPUS_ORB_KEY);

/**
 * While a turn runs, the turn's current face (`runningState`: idle while it
 * looks around, its dots while the model reasons or a step runs). When it
 * finishes: done if it did real work (`madeSomething`: created, found or
 * changed something; unless the chat row already started the beat), else
 * straight to idle. Resets to idle when the panel unmounts.
 */
export const useDriveOrb = (
  key: string,
  isRunning: boolean,
  madeSomething = false,
  turnId: string | null = null,
  runningState: OrbStateId = "thinking",
): void => {
  // Read when the turn ends, not as triggers.
  const latest = useRef({ madeSomething, turnId });
  latest.current = { madeSomething, turnId };
  const wasRunning = useRef(false);
  useEffect(() => {
    const store = useOrbStateStore.getState();
    const beat = store.beats[key];
    const id = latest.current.turnId;
    const played = id && beat?.turnId === id ? beat.kind : null;
    if (isRunning) {
      wasRunning.current = true;
      // A beat the row started for this turn plays out over the rest of it.
      if (!played) store.setState(key, runningState);
      return;
    }
    if (!wasRunning.current) return;
    wasRunning.current = false;
    // A turn that did real work ends with done (the spin is kept, not used
    // for now); a plain answer rests. The chat row may already have started
    // the beat: then it just plays out.
    if (played) return;
    if (latest.current.madeSomething) store.playBeat(key, id, "done");
    else store.setState(key, "idle");
  }, [key, isRunning, runningState]);
  useEffect(
    () => () => useOrbStateStore.getState().setState(key, "idle"),
    [key],
  );
};

export const useDriveAskOpusOrb = (
  isRunning: boolean,
  madeSomething = false,
  turnId: string | null = null,
  runningState: OrbStateId = "thinking",
): void =>
  useDriveOrb(ASK_OPUS_ORB_KEY, isRunning, madeSomething, turnId, runningState);
